import { Button } from '@/components/ui/Button'
import { BrandLockup } from '@/components/ui/BrandLockup'
import { useToastOptional } from '@/components/ui/Toast'
import { typo } from '@/lib/typo'

/**
 * Edge screens of the admin (WP-08, design §6.10): a cause, one action and a code for support.
 * `inline` sits inside the shell (`<main>`): the rail survives a broken page. `full` is the last
 * resort when the shell itself failed: centred on the page with the brand lockup on top.
 *
 * `mark`: `error` — the danger ✕ of a failure (a crash, a page not found), announced as an
 * alert; `update` — a neutral ↻ in ink-2 for «Вышла новая версия»: nothing failed, the tab is
 * just old, so no error colour (as the Mini App's «Вне Telegram»).
 */
function EdgeScreen({
  title,
  body,
  action,
  secondary,
  code,
  layout,
  mark = 'error',
}: {
  title: string
  body: string
  action: { label: string; onClick: () => void }
  secondary?: { label: string; onClick: () => void }
  code?: string
  layout: 'inline' | 'full'
  mark?: 'error' | 'update'
}) {
  const toast = useToastOptional()

  async function copy() {
    if (!code) return
    try {
      await navigator.clipboard.writeText(`${title}: код ${code}`)
      toast?.success('Скопировано')
    } catch {
      toast?.error('Не удалось скопировать: выделите код вручную')
    }
  }

  const content = (
    <div role={mark === 'error' ? 'alert' : 'status'} className="flex max-w-prose flex-col items-center gap-3 text-center">
      <h1 className="text-title-1 font-semibold text-ink-1 [overflow-wrap:anywhere]">
        <span aria-hidden className={mark === 'error' ? 'mr-2 text-danger' : 'mr-2 text-ink-2'}>
          {mark === 'error' ? '✕' : '↻'}
        </span>
        {typo(title)}
      </h1>
      <p className="text-body text-ink-2">{typo(body)}</p>
      <div className="mt-2 flex flex-wrap items-center justify-center gap-3">
        <Button variant="primary" size="lg" onClick={action.onClick}>
          {action.label}
        </Button>
        {secondary && (
          <Button variant="ghost" size="lg" onClick={secondary.onClick}>
            {secondary.label}
          </Button>
        )}
      </div>
      {code && (
        <button
          type="button"
          onClick={() => void copy()}
          title="Скопировать код для поддержки"
          className="tnum rounded-xs text-caption text-ink-3 hover:text-ink-2"
        >
          Код {code}
        </button>
      )}
    </div>
  )

  if (layout === 'inline') return <div className="flex justify-center px-4 py-16">{content}</div>
  return (
    <main className="grid min-h-dvh place-items-center bg-void px-4">
      <div className="flex flex-col items-center gap-8">
        <BrandLockup size={32} />
        {content}
      </div>
    </main>
  )
}

function reload() {
  window.location.reload()
}

/** The open tab runs an old build: a reload fixes it, a retry never would. */
export function NewVersionScreen({ layout = 'inline' }: { layout?: 'inline' | 'full' }) {
  return (
    <EdgeScreen
      layout={layout}
      mark="update"
      title="Вышла новая версия админки"
      body="Эта вкладка открыта со старой версии, и раздел не загрузился. Обновите страницу — несохранённые изменения на ней пропадут."
      action={{ label: 'Обновить страницу', onClick: reload }}
    />
  )
}

/** A render error in a page (or, `full`, in the shell). «Повторить» renders it again. */
export function CrashScreen({
  layout = 'inline',
  onRetry,
  error,
}: {
  layout?: 'inline' | 'full'
  onRetry: () => void
  error?: unknown
}) {
  return (
    <EdgeScreen
      layout={layout}
      title="Страница не открылась"
      body="Ошибка в коде страницы, данные не изменились. Повторите; если не проходит — обновите страницу и передайте код разработчику."
      action={{ label: 'Повторить', onClick: onRetry }}
      secondary={{ label: 'Обновить страницу', onClick: reload }}
      code={crashCode(error)}
    />
  )
}

/** A short support code: «UI-TypeError». The message itself stays in the console. */
export function crashCode(error: unknown): string {
  const name = error instanceof Error && /^[A-Za-z]+$/.test(error.name) ? error.name : 'Error'
  return `UI-${name}`
}
